import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { LoadingState, EmptyState } from '../../components/ui/Spinner';
import { formatAmount, formatDate, calculateDisplayRate, formatDisplayRate } from '../../lib/format';

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

interface CashflowReportProps {
  entityId: string;
  periodId: string;
}

export default function CashflowReport({ entityId, periodId }: CashflowReportProps) {
  const { t } = useTranslation('reports');
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = (searchParams.get('viewMode') as 'summary' | 'detailed') || 'detailed';
  const page = parseInt(searchParams.get('page') || '1', 10) || 1;

  const setViewMode = useCallback((mode: 'summary' | 'detailed') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (mode === 'detailed') next.delete('viewMode'); else next.set('viewMode', mode);
      next.delete('page');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setPage = useCallback((p: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (p === 1) next.delete('page'); else next.set('page', String(p));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const periodParam = periodId ? `&periodId=${periodId}` : '';

  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['report-cashflow-summary', entityId, periodId],
    queryFn: async () => {
      const res = await fetch(`/api/reports/cashflow?${periodParam.slice(1)}`, {
        headers: { 'X-Entity-Id': entityId },
        credentials: 'include',
      });
      return res.json();
    },
    enabled: viewMode === 'summary',
  });

  const { data: detailedData, isLoading: detailedLoading } = useQuery({
    queryKey: ['report-cashflow-detailed', entityId, periodId, page],
    queryFn: async () => {
      const res = await fetch(`/api/reports/cashflow?detailed=true&page=${page}&limit=50${periodParam}`, {
        headers: { 'X-Entity-Id': entityId },
        credentials: 'include',
      });
      return res.json();
    },
    enabled: viewMode === 'detailed',
  });

  const isLoading = viewMode === 'summary' ? summaryLoading : detailedLoading;

  if (isLoading) return <LoadingState message={t('cashflow.loading')} />;

  return (
    <div className="report-content">
      <div className="card mb-4">
        <div className="view-toggle">
          <button
            className={`btn ${viewMode === 'detailed' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('detailed')}
          >
            {t('views.detailed')}
          </button>
          <button
            className={`btn ${viewMode === 'summary' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('summary')}
          >
            {t('views.summary')}
          </button>
        </div>
      </div>

      {viewMode === 'detailed' ? (
        <DetailedCashflowTable data={detailedData} page={page} setPage={setPage} />
      ) : (
        <SummaryCashflow data={summaryData} />
      )}

      <style>{`
        .view-toggle {
          display: flex;
          gap: 8px;
        }
      `}</style>
    </div>
  );
}

function DetailedCashflowTable({ data, page, setPage }: { data: any; page: number; setPage: (p: number) => void }) {
  const { t } = useTranslation('reports');
  
  if (!data?.data?.length) {
    return (
      <div className="card">
        <EmptyState title={t('cashflow.empty.title')} description={t('cashflow.empty.description')} />
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <div className="table-scroll">
          <table className="table table-compact">
            <thead>
              <tr>
                <th>{t('columns.date')}</th>
                <th>{t('columns.type')}</th>
                <th>{t('columns.client')}</th>
                <th className="text-right">{t('columns.amountIn')}</th>
                <th className="text-right">{t('columns.amountOut')}</th>
                <th className="text-right">{t('columns.rate')}</th>
                <th>{t('columns.status')}</th>
                <th className="text-right">{t('columns.debt')}</th>
                <th>{t('columns.employee')}</th>
                <th>{t('columns.comment')}</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((tx: any) => (
                <tr key={tx.id} className={tx.status === 'open' ? 'row-open' : ''}>
                  <td className="text-nowrap">{formatDate(tx.transactionDate || tx.createdAt)}</td>
                  <td><span className={`badge badge-${tx.type}`}>{capitalize(tx.type)}</span></td>
                  <td>{tx.clientName || '-'}</td>
                  <td className="text-right font-mono text-success">
                    {tx.amountIn ? `${formatAmount(tx.amountIn, tx.currencyIn)} ${tx.currencyIn}` :
                     tx.amount && tx.type === 'deposit' ? `${formatAmount(tx.amount, tx.currency)} ${tx.currency}` : '-'}
                  </td>
                  <td className="text-right font-mono text-error">
                    {tx.amountOut ? `${formatAmount(tx.amountOut, tx.currencyOut)} ${tx.currencyOut}` :
                     tx.amount && tx.type !== 'deposit' ? `${formatAmount(tx.amount, tx.currency)} ${tx.currency}` : '-'}
                  </td>
                  <td className="text-right font-mono">
                    {formatDisplayRate(calculateDisplayRate(tx))}
                  </td>
                  <td><span className={`badge badge-${tx.status}`}>{capitalize(tx.status)}</span></td>
                  <td className="text-right font-mono text-error">
                    {tx.debtAmount && parseFloat(tx.debtAmount) > 0 ? formatAmount(tx.debtAmount) : '-'}
                  </td>
                  <td>{tx.employeeName}</td>
                  <td className="comment-cell" title={tx.comment || ''}>{tx.comment || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.meta?.totalPages > 1 && (
        <div className="pagination">
          <button className="btn btn-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>
            &larr; {t('pagination.prev')}
          </button>
          <span>{t('pagination.page')} {page} {t('pagination.of')} {data.meta.totalPages}</span>
          <button className="btn btn-secondary" disabled={page >= data.meta.totalPages} onClick={() => setPage(page + 1)}>
            {t('pagination.next')} &rarr;
          </button>
        </div>
      )}

      <style>{`
        .table-scroll { overflow-x: auto; }
        .table-compact th, .table-compact td { padding: 8px 12px; font-size: 13px; }
        .text-nowrap { white-space: nowrap; }
        .font-mono { font-family: monospace; }
        .row-open { background: var(--badge-open-bg); }
        .comment-cell { max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .pagination { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 16px; }
      `}</style>
    </>
  );
}

function SummaryCashflow({ data }: { data: any }) {
  const { t } = useTranslation('reports');
  
  if (!data?.summary?.length) {
    return (
      <div className="card">
        <EmptyState title={t('cashflow.noData.title')} description={t('cashflow.noData.description')} />
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="card-title mb-4">{t('cashflow.summaryTitle')}</h3>
      <table className="table">
        <thead>
          <tr>
            <th>{t('columns.type')}</th>
            <th>{t('columns.currency')}</th>
            <th className="text-right">{t('totalIn')}</th>
            <th className="text-right">{t('totalOut')}</th>
            <th className="text-right">{t('columns.count')}</th>
          </tr>
        </thead>
        <tbody>
          {data.summary.map((s: any, i: number) => (
            <tr key={i}>
              <td><span className={`badge badge-${s.type}`}>{capitalize(s.type)}</span></td>
              <td>{s.currency}</td>
              <td className="text-right font-mono text-success">{s.totalIn ? formatAmount(s.totalIn) : '-'}</td>
              <td className="text-right font-mono text-error">{s.totalOut ? formatAmount(s.totalOut) : '-'}</td>
              <td className="text-right">{s.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
